import { useState, useEffect, useCallback } from 'react';
import { pushNotificationApi } from '../services/api/push-notifications';

// Helper function to convert browser PushSubscription to our custom format
function convertBrowserSubscription(browserSubscription: PushSubscription): import('../services/api/push-notifications').PushSubscription {
  return {
    endpoint: browserSubscription.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(browserSubscription.getKey('p256dh')!),
      auth: arrayBufferToBase64(browserSubscription.getKey('auth')!)
    }
  };
}

// Helper function to convert ArrayBuffer to base64 string
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

interface UsePushNotificationsReturn {
  isSupported: boolean;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  vapidPublicKey: string | null;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<void>;
  sendTestNotification: (title: string, body: string) => Promise<void>;
}

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  // Vérifier la compatibilité et récupérer la clé publique VAPID
  useEffect(() => {
    const checkSupport = async () => {
      try {
        console.log('🔍 Vérification du support des push notifications...');

        // Vérifier si les push notifications sont supportées
        if (!('serviceWorker' in navigator)) {
          console.log('❌ Service Worker non supporté par le navigateur');
          setIsSupported(false);
          setError('Les push notifications ne sont pas supportées par votre navigateur');
          return;
        }

        if (!('PushManager' in window)) {
          console.log('❌ PushManager non supporté par le navigateur');
          setIsSupported(false);
          setError('Les push notifications ne sont pas supportées par votre navigateur');
          return;
        }

        console.log('✅ Navigateur supporte les push notifications');

        // Récupérer la clé publique VAPID
        console.log('🔑 Récupération de la clé VAPID...');
        const publicKey = await pushNotificationApi.getVapidPublicKey();
        if (!publicKey) {
          console.log('❌ Clé VAPID non configurée sur le serveur');
          setError('Les clés VAPID ne sont pas configurées sur le serveur');
          setIsSupported(false);
          return;
        }

        console.log('✅ Clé VAPID récupérée:', publicKey.substring(0, 20) + '...');
        setVapidPublicKey(publicKey);
        setIsSupported(true);

        // Enregistrer le service worker
        console.log('👷 Enregistrement du service worker...');
        const swRegistration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });
        console.log('✅ Service worker enregistré:', swRegistration);
        
        // Attendre que le service worker soit activé
        if (swRegistration.waiting) {
          console.log('⏳ Service worker en attente, activation...');
          swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        
        // Attendre que le service worker soit actif
        if (!swRegistration.active) {
          console.log('⏳ Attente de l\'activation du service worker...');
          await new Promise<void>((resolve) => {
            const checkActive = () => {
              if (swRegistration.active) {
                resolve();
              } else {
                setTimeout(checkActive, 100);
              }
            };
            checkActive();
          });
        }
        
        console.log('✅ Service worker maintenant actif');
        setRegistration(swRegistration);

        // Vérifier l'état d'abonnement actuel
        await checkSubscriptionStatus(swRegistration);
      } catch (err) {
        console.error('❌ Erreur lors de la vérification des push notifications:', err);
        setError('Erreur lors de l\'initialisation des push notifications');
        setIsSupported(false);
      }
    };

    checkSupport();
  }, []);

  // Vérifier l'état d'abonnement
  const checkSubscriptionStatus = useCallback(async (swRegistration: ServiceWorkerRegistration) => {
    try {
      const subscription = await swRegistration.pushManager.getSubscription();
      const isClientSubscribed = !!subscription;
      
      // Vérifier également si l'abonnement est enregistré côté serveur
      if (isClientSubscribed) {
        try {
          const serverSubscriptions = await pushNotificationApi.getSubscriptions();
          const isServerSubscribed = serverSubscriptions.length > 0;
          
          if (!isServerSubscribed) {
            console.warn('⚠️ Abonnement client présent mais absent côté serveur, réenregistrement...');
            const customSubscription = convertBrowserSubscription(subscription!);
            await pushNotificationApi.subscribe(customSubscription);
            console.log('✅ Abonnement réenregistré côté serveur');
          }
        } catch (serverError) {
          console.error('Erreur lors de la vérification côté serveur:', serverError);
        }
      }
      
      setIsSubscribed(isClientSubscribed);
    } catch (err) {
      console.error('Erreur lors de la vérification de l\'abonnement:', err);
    }
  }, []);

  // S'abonner aux push notifications
  const subscribe = useCallback(async () => {
    if (!registration || !vapidPublicKey) {
      setError('Service worker ou clé VAPID non disponible');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Vérifier que le service worker est actif avant de s'abonner
      if (!registration.active) {
        console.log('⏳ Service worker pas encore actif, attente de l\'activation...');
        // Attendre que le service worker soit activé
        await new Promise<void>((resolve, reject) => {
          const checkActive = () => {
            if (registration.active) {
              resolve();
            } else {
              setTimeout(checkActive, 100);
            }
          };
          checkActive();
          
          // Timeout après 5 secondes
          setTimeout(() => reject(new Error('Timeout: Service worker non activé')), 5000);
        });
        console.log('✅ Service worker maintenant actif');
      }

      // Valider la clé VAPID
      console.log('🔑 Validation de la clé VAPID:', vapidPublicKey.substring(0, 20) + '...');
      if (!vapidPublicKey || vapidPublicKey.length < 50) {
        setError('Clé VAPID invalide ou trop courte');
        return false;
      }

      // Convertir la clé VAPID au format Uint8Array
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      console.log('🔑 Clé VAPID convertie en Uint8Array, longueur:', applicationServerKey.length);

      // Tenter l'abonnement avec gestion d'erreur détaillée
      console.log('📱 Tentative d\'abonnement aux push notifications...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as any,
      });

      console.log('✅ Abonnement push réussi côté client, endpoint:', subscription.endpoint.substring(0, 50) + '...');

      // Convert browser subscription to our custom format and send to server
      const customSubscription = convertBrowserSubscription(subscription);
      console.log('📤 Envoi de l\'abonnement au serveur...');
      await pushNotificationApi.subscribe(customSubscription);

      setIsSubscribed(true);
      console.log('✅ Abonnement aux push notifications réussi côté serveur');
      return true;
    } catch (err: any) {
      console.error('❌ Erreur détaillée lors de l\'abonnement:', err);
      console.error('❌ Type d\'erreur:', err.name);
      console.error('❌ Message d\'erreur:', err.message);
      console.error('❌ Stack:', err.stack);

      // Gestion spécifique des erreurs FCM
      if (err.name === 'NotAllowedError') {
        setError('Vous avez refusé les permissions de notification');
      } else if (err.name === 'AbortError') {
        if (err.message.includes('push service error')) {
          setError('Erreur du service push (FCM). Vérifiez les clés VAPID et la configuration HTTPS.');
        } else {
          setError('Service worker non disponible pour l\'abonnement');
        }
      } else if (err.name === 'InvalidStateError') {
        setError('État invalide - peut-être déjà abonné');
      } else if (err.name === 'NetworkError') {
        setError('Erreur réseau lors de l\'abonnement');
      } else {
        setError(`Erreur technique: ${err.message || 'Erreur inconnue'}`);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [registration, vapidPublicKey]);

  // Se désabonner des push notifications
  const unsubscribe = useCallback(async () => {
    if (!registration) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Se désabonner côté client
        await subscription.unsubscribe();

        // Se désabonner côté serveur
        await pushNotificationApi.unsubscribe(subscription.endpoint);

        setIsSubscribed(false);
        console.log('✅ Désabonnement des push notifications réussi');
      }
    } catch (err) {
      console.error('❌ Erreur lors du désabonnement:', err);
      setError('Erreur lors du désabonnement des push notifications');
    } finally {
      setIsLoading(false);
    }
  }, [registration]);

  // Envoyer une notification de test
  const sendTestNotification = useCallback(async (title: string, body: string) => {
    try {
      await pushNotificationApi.sendTestNotification(title, body);
      console.log('✅ Notification de test envoyée');
    } catch (err) {
      console.error('❌ Erreur lors de l\'envoi de la notification de test:', err);
      setError('Erreur lors de l\'envoi de la notification de test');
    }
  }, []);

  // Fonction utilitaire pour convertir la clé VAPID
  const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  };

  return {
    isSupported,
    isSubscribed,
    isLoading,
    error,
    vapidPublicKey,
    subscribe,
    unsubscribe,
    sendTestNotification,
  };
};